import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  output,
  viewChild
} from '@angular/core';
import { actionsWidths, resultsWidths, statsWidths } from "../../configs/columns.config";
import {
  Manager,
  PlanFactTableDataGroupedByMonths,
  Product,
  Report,
  Team,
  UpdateValueParams
} from "../../types/table-data.types";
import { defaultDeleteConfirmationParams } from "../../../../../core/models/default-delete-confirmation-params";
import { ConfirmService } from "../../../../../core/services/confirm.service";
import { PlanFactApiService } from "../../services/plan-fact-api.service";
import { UtilityService } from "../../../../../core/services/utility.service";
import { PlanFactService } from "../../services/plan-fact.service";
import { defaultPlatformPlans } from "../../configs/default-platforms.config";
import { DeleteFocusDirective } from "../../../../../shared/directives/delete-focus.directive";
import {
  UpdateInputNumberComponent
} from "../../../../../shared/components/update-input/update-input-number.component";
import { FactValueComponent } from "../fact-value/fact-value.component";
import { GroupedValueColumnsComponent } from "../grouped-value-columns/grouped-value-columns.component";
import { ClickOutsideDirective } from "../../../../../shared/directives/click-outside.directive";

@Component({
  selector: 'app-plan-fact-managers-rows',
  standalone: true,
  imports: [
    DeleteFocusDirective,
    UpdateInputNumberComponent,
    FactValueComponent,
    GroupedValueColumnsComponent,
    ClickOutsideDirective
  ],
  templateUrl: './plan-fact-managers-rows.component.html',
  styleUrls: [
    './plan-fact-managers-rows.component.scss',
    '../plan-fact-components-styles.scss'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlanFactManagersRowsComponent {
  managerReports = input.required<Array<Report>>();
  selectedYear = input.required<number>();
  monthData = input.required<PlanFactTableDataGroupedByMonths>();
  product = input.required<Product>();
  isAssignedManager = input.required<boolean>();
  team = input.required<Team>();
  updateValue = output<UpdateValueParams>();
  managersListRef = viewChild<ElementRef<HTMLElement>>('managersList');

  private confirmService: ConfirmService = inject(ConfirmService);
  private planFactApiService: PlanFactApiService = inject(PlanFactApiService);
  private utilityService: UtilityService = inject(UtilityService);
  private planFactService: PlanFactService = inject(PlanFactService);

  public readonly actionsWidths = actionsWidths;
  public readonly resultsWidths = resultsWidths;
  public readonly statsWidths = statsWidths;
  public selectedManagerIndex: number = -1;

  protected async handleDeleteManagerButtonClick(id: number, monthNumber: number): Promise<void> {
    const isConfirmed = await this.confirmService.getDeleteConfirmation({
      ...defaultDeleteConfirmationParams,
      titleText: 'Удалить менеджера',
      description: '<p> Вы уверены, что хотите удалить менеджера?</p>'
    });
    if (!isConfirmed) { return; }

    this.planFactApiService.deleteManager(id).subscribe({
      next: () => {
        this.planFactService.openMonthData({
          monthNumber: monthNumber,
          selectedYear: this.selectedYear()
        }).then()
      }
    })
  }

  protected formatNumberValue(value: number | string, fractional: number = 0): string {
    return this.utilityService.formatNumberForDisplay({
      value: +value,
      fractional: { value: fractional },
      numberSeparator: { separator: ' ' }
    });
  }

  protected getUnAddedManagers(team: Team): Array<Manager> {
    return team.managers.filter((manager: Manager) => {
      return team.managerReports.every((managerReport: Report) => managerReport.manager.id !== +manager.id);
    })
  }

  protected addManagerToManagerReports(month: number, reviewerPlanFactId: number, managerId: number): void {
    const params = {
      reviewerPlanFactId,
      managerId,
      month: `01.0${month}.${this.selectedYear()}`,
      ...defaultPlatformPlans
    }

    this.planFactApiService.addManagerToList(params).subscribe({
      next: () => {
        this.planFactService.openMonthData({
          monthNumber: month,
          selectedYear: this.selectedYear()
        }).then()
      }
    })
  }

  protected closeUnAddedManagersList(team: Team): void {
    team.isUnAddedManagersListOpen = false;
  }

  protected toggleUnUnAddedManagersList(team: Team): void {
    team.isUnAddedManagersListOpen ? this.closeUnAddedManagersList(team) : this.openUnAddedManagersList(team);
  }

  protected openUnAddedManagersList(team: Team): void {
    team.isUnAddedManagersListOpen = true;
    this.selectedManagerIndex = -1;
    setTimeout(() => this.managersListRef()?.nativeElement?.focus());
  }

  public handleUpdateValue(updateValueParams: UpdateValueParams): void {
    this.updateValue.emit(updateValueParams);
  }

  public handleKeyDown(event: KeyboardEvent, team: any) {
    const managers = this.getUnAddedManagers(team);
    const managerCount = managers.length;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedManagerIndex = (this.selectedManagerIndex + 1) % managerCount;
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selectedManagerIndex = (this.selectedManagerIndex - 1 + managerCount) % managerCount;
        break;
      case 'Enter':
        if (this.selectedManagerIndex >= 0 && this.selectedManagerIndex < managerCount) {
          const manager = managers[this.selectedManagerIndex];
          this.addManagerToManagerReports(this.monthData().monthNumber, team.id, +manager.id);
        }
        break;
    }
  }
}
